import {
  getSharedScheduleErrorCode,
  resolveMembershipLifecycleError,
} from "../features/sharedMembership/errors";

/**
 * 담당 강사 변경(단건 `change-assigned-coach-v2`, 일괄 `bulk-change-assigned-coach-v2`) 실패 안내.
 *
 * 예전에는 실패하면 400·409 가 undefined 로 삼켜져 화면에 아무 안내가 없었다.
 * api 계층이 `{ status, data }` 를 그대로 넘기고, 이 파일이 사람이 읽을 문구로 바꾼다.
 *
 * 영어 서버 메시지('Unknown error', 'Not found Member' 등)는 관리자에게 의미가 없으므로
 * 그대로 보여주지 않고 기본 문구로 바꾼다. 한국어 메시지만 통과시킨다.
 */

export type ChangeCoachFailure = {
  /** HTTP 상태. 응답 자체가 없으면(네트워크 오류) 0. */
  status?: number;
  data?: unknown;
};

export type ChangeCoachFailureReason =
  | "share_owner"
  | "not_connected"
  | "transfer_conflict"
  | "refund_in_progress"
  | "refund_review_required"
  | "permission"
  | "network"
  | "blocked"
  | "other";

export const CHANGE_COACH_DEFAULT_ERROR_MESSAGE =
  "담당 강사를 변경하지 못했어요.";

export const CHANGE_COACH_SHARE_OWNER_MESSAGE =
  "이 회원이 공유 수강권을 가지고 있어서, 공유를 먼저 끝내야 담당 강사를 바꿀 수 있어요.";

export const CHANGE_COACH_NOT_CONNECTED_MESSAGE =
  "이미 다른 강사로 변경됐거나 연결이 끊긴 회원이에요. 최신 정보로 새로고침한 뒤 다시 확인해 주세요.";

export const CHANGE_COACH_REFUND_IN_PROGRESS_MESSAGE =
  "환불이 진행 중인 수강권이 있어 지금은 변경할 수 없어요.";

export const CHANGE_COACH_REFUND_REVIEW_MESSAGE =
  "환불 처리에 관리자 확인이 필요한 수강권이 있어 지금은 변경할 수 없어요.";

export const CHANGE_COACH_PERMISSION_MESSAGE =
  "담당 강사를 변경할 권한이 없어요. 계정 권한을 확인해 주세요.";

export const CHANGE_COACH_AMBIGUOUS_MESSAGE =
  "담당 강사가 여러 명인 회원이에요. 회원 상세에서 한 명씩 바꿔 주세요.";

export const CHANGE_COACH_NETWORK_MESSAGE =
  "네트워크 상태를 확인한 뒤 다시 시도해 주세요.";

/** 서버가 연결이 없을 때 던지는 영어 메시지(rappo center.controller `getAssignedRapportForCoachChange`). */
const NOT_CONNECTED_SERVER_MESSAGE =
  "Member is not connected to the specified coach";

/** rappo `membershipSalesRefundProtection.service.ts` 의 미해결 환불 작업 409.
 *  진행 중과 "관리자 확인 필요"(멈춰서 확인을 기다림)는 다른 상황이라 문구를 나눈다. */
const REFUND_IN_PROGRESS_ERROR_CODE = "MEMBERSHIP_SALES_REFUND_OPERATION_IN_PROGRESS";
const REFUND_REVIEW_REQUIRED_ERROR_CODE = "MEMBERSHIP_SALES_REFUND_REVIEW_REQUIRED";
/** 기존 담당 강사를 안 보냈는데 그 센터 담당 강사가 둘 이상인 회원(단건 409, BACKEND_HANDOFF_N §4). */
const ASSIGNMENT_AMBIGUOUS_ERROR_CODE = "MEMBER_COACH_ASSIGNMENT_AMBIGUOUS";

/**
 * 일괄 변경의 실패 행에는 errorCode 가 없고 message 문자열만 온다.
 * 같은 원인을 단건과 같은 문구로 보이게 하려고 서버 메시지 원문을 알아 둔다.
 */
const REFUND_IN_PROGRESS_SERVER_MESSAGE = "원매출 환불 작업이 아직 완료되지 않았습니다.";
const REFUND_REVIEW_REQUIRED_SERVER_MESSAGE = "원매출 환불 작업에 관리자 확인이 필요합니다.";
/** 일괄 실패 행의 같은 사유(rappo center.controller N-0b). */
const ASSIGNMENT_AMBIGUOUS_SERVER_MESSAGE =
  "이 센터에 담당 강사가 여럿인 회원입니다. 변경할 기존 담당 강사를 지정해 주세요.";

const HANGUL_PATTERN = /[가-힣]/;

/** 서버의 한국어 사유도 결과 모달의 다른 문구와 같은 해요체로 맞춘다. */
/** 한글 음절에 받침이 있는가. "상태입니다" → "상태예요", "회원입니다" → "회원이에요". */
const hasFinalConsonant = (syllable: string): boolean => {
  const code = syllable.charCodeAt(0) - 0xac00;
  return code >= 0 && code <= 11171 && code % 28 !== 0;
};

const toHaeyoStyle = (message: string): string =>
  message
    .replace(/합니다(?=\s|[.!?]|$)/g, "해요")
    .replace(/([가-힣]?)입니다(?=\s|[.!?]|$)/g, (_m, prev: string) =>
      prev && !hasFinalConsonant(prev) ? `${prev}예요` : `${prev}이에요`,
    )
    .replace(/됩니다(?=\s|[.!?]|$)/g, "돼요")
    .replace(/습니다(?=\s|[.!?]|$)/g, "어요");

const readMessage = (data: unknown): string => {
  if (!data || typeof data !== "object") return "";
  const message = (data as { message?: unknown }).message;
  return typeof message === "string" ? message.trim() : "";
};

/** 문의 정보처럼 진단용 값이 필요한 UI에서만 쓴다. 화면 문구에는 오류 코드를 노출하지 않는다. */
export const getChangeCoachErrorCode = (
  failure: ChangeCoachFailure | null | undefined,
): string | undefined => getSharedScheduleErrorCode({ data: failure?.data });

/**
 * 기존 문구 판정과 같은 순서로 UI 행동만 분류한다.
 *
 * 문구 헬퍼와 별도 분류를 둔 이유는 같은 안내 문구라도 새로고침·재시도·문의처럼
 * 사용자가 다음에 할 일이 다르기 때문이다.
 */
export const getChangeCoachFailureReason = (
  failure: ChangeCoachFailure | null | undefined,
): ChangeCoachFailureReason => {
  const status = typeof failure?.status === "number" ? failure.status : 0;
  const data = failure?.data;
  const errorCode = getSharedScheduleErrorCode({ data });

  if (errorCode === "SHARE_OWNER_DISCONNECT_BLOCKED") return "share_owner";
  if (errorCode === "MEMBERSHIP_TRANSFER_LINK_CONFLICT")
    return "transfer_conflict";
  if (errorCode === REFUND_IN_PROGRESS_ERROR_CODE)
    return "refund_in_progress";
  if (errorCode === REFUND_REVIEW_REQUIRED_ERROR_CODE)
    return "refund_review_required";
  // 같은 요청을 다시 보내도 같은 결과라 다시 시도를 권하지 않는다.
  if (errorCode === ASSIGNMENT_AMBIGUOUS_ERROR_CODE) return "blocked";

  // 기존 문구 함수도 수강권 생애주기 오류를 status 보다 먼저 판정한다.
  // 종료된 수강권·탈퇴 회원 등은 같은 요청을 다시 보내도 결과가 같으므로 다시 시도를 권하지 않는다.
  if (resolveMembershipLifecycleError({ data })) return "blocked";
  if (status === 0) return "network";
  if (status === 401 || status === 403) return "permission";
  if (readMessage(data) === NOT_CONNECTED_SERVER_MESSAGE)
    return "not_connected";
  // 입력 검증 실패(400·422)도 다시 보내면 똑같이 실패한다.
  if (status === 400 || status === 422) return "blocked";
  return "other";
};

/** 서버 메시지 원문(주로 일괄 실패 행의 `error`)을 안내 문구로 바꾼다. */
export const getChangeCoachErrorMessageFromServerText = (
  serverMessage: unknown,
): string => {
  const text = typeof serverMessage === "string" ? serverMessage.trim() : "";
  if (!text) return CHANGE_COACH_DEFAULT_ERROR_MESSAGE;
  if (text === NOT_CONNECTED_SERVER_MESSAGE)
    return CHANGE_COACH_NOT_CONNECTED_MESSAGE;
  if (text === REFUND_IN_PROGRESS_SERVER_MESSAGE)
    return CHANGE_COACH_REFUND_IN_PROGRESS_MESSAGE;
  if (text === REFUND_REVIEW_REQUIRED_SERVER_MESSAGE)
    return CHANGE_COACH_REFUND_REVIEW_MESSAGE;
  if (text === ASSIGNMENT_AMBIGUOUS_SERVER_MESSAGE)
    return CHANGE_COACH_AMBIGUOUS_MESSAGE;
  return HANGUL_PATTERN.test(text)
    ? toHaeyoStyle(text)
    : CHANGE_COACH_DEFAULT_ERROR_MESSAGE;
};

/** 단건·일괄 요청 전체 실패(`{ status, data }`)를 안내 문구로 바꾼다. */
export const getChangeCoachErrorMessage = (
  failure: ChangeCoachFailure | null | undefined,
): string => {
  const status = typeof failure?.status === "number" ? failure.status : 0;
  const data = failure?.data;
  const errorCode = getSharedScheduleErrorCode({ data });

  if (errorCode === "SHARE_OWNER_DISCONNECT_BLOCKED")
    return CHANGE_COACH_SHARE_OWNER_MESSAGE;
  // 인계 상태 꼬임은 서버가 관리자에게 보여줄 한국어 문구를 준다.
  if (errorCode === "MEMBERSHIP_TRANSFER_LINK_CONFLICT") {
    return getChangeCoachErrorMessageFromServerText(readMessage(data));
  }
  if (errorCode === REFUND_IN_PROGRESS_ERROR_CODE)
    return CHANGE_COACH_REFUND_IN_PROGRESS_MESSAGE;
  if (errorCode === REFUND_REVIEW_REQUIRED_ERROR_CODE)
    return CHANGE_COACH_REFUND_REVIEW_MESSAGE;
  if (errorCode === ASSIGNMENT_AMBIGUOUS_ERROR_CODE)
    return CHANGE_COACH_AMBIGUOUS_MESSAGE;

  // 종료된 수강권·환불행·탈퇴 회원은 다른 화면과 같은 문구를 쓴다.
  const lifecycleError = resolveMembershipLifecycleError({ data });
  if (lifecycleError) return toHaeyoStyle(lifecycleError.message);

  if (status === 0) return CHANGE_COACH_NETWORK_MESSAGE;
  if (status === 401 || status === 403) return CHANGE_COACH_PERMISSION_MESSAGE;

  return getChangeCoachErrorMessageFromServerText(readMessage(data));
};

type BulkChangeCoachResultRow = {
  memberId?: unknown;
  success?: unknown;
  error?: unknown;
};

export type BulkChangeCoachResultItem = {
  memberId: string;
  name: string;
  reason: string;
};

export type BulkChangeCoachResult = {
  totalCount: number;
  successCount: number;
  failureCount: number;
  succeeded: BulkChangeCoachResultItem[];
  failed: BulkChangeCoachResultItem[];
};

/** 실패 사유를 몇 건까지 보여줄지. 넘치면 "외 N명". */
export const BULK_CHANGE_COACH_FAILURE_PREVIEW_LIMIT = 5;

/** 일괄 응답을 결과 모달에서 바로 쓸 수 있는 회원별 성공·실패 목록으로 바꾼다. */
export const getBulkChangeCoachResult = (
  data: unknown,
  resolveMemberName?: (memberId: string) => string | undefined,
): BulkChangeCoachResult | null => {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  const rows: BulkChangeCoachResultRow[] = Array.isArray(record.results)
    ? (record.results as BulkChangeCoachResultRow[])
    : [];
  const failedRows = rows.filter((row) => row && row.success === false);
  const succeededRows = rows.filter((row) => row && row.success === true);

  const reportedFailureCount =
    typeof record.failureCount === "number" &&
    Number.isSafeInteger(record.failureCount)
      ? record.failureCount
      : failedRows.length;
  const failureCount = Math.max(reportedFailureCount, failedRows.length);
  if (failureCount <= 0) return null;

  const reportedSuccessCount =
    typeof record.successCount === "number" &&
    Number.isSafeInteger(record.successCount)
      ? record.successCount
      : succeededRows.length;
  const successCount = Math.max(reportedSuccessCount, succeededRows.length);
  const reportedTotalCount =
    typeof record.totalProcessed === "number" &&
    Number.isSafeInteger(record.totalProcessed)
      ? record.totalProcessed
      : rows.length;
  const totalCount = Math.max(
    reportedTotalCount,
    successCount + failureCount,
    rows.length,
  );

  const toResultItem = (
    row: BulkChangeCoachResultRow,
  ): BulkChangeCoachResultItem => {
    const memberId = typeof row.memberId === "string" ? row.memberId : "";
    return {
      memberId,
      name: (memberId && resolveMemberName?.(memberId)) || "회원 이름 미확인",
      reason: getChangeCoachErrorMessageFromServerText(row.error),
    };
  };

  return {
    totalCount,
    successCount,
    failureCount,
    succeeded: succeededRows.map(toResultItem),
    failed: failedRows.map(toResultItem),
  };
};

/**
 * 일괄 변경 응답(HTTP 200)에서 실패가 있으면 안내 본문을 만든다. 실패가 없으면 null.
 *
 * `failureCount` 를 먼저 믿고, 없거나 손상됐으면 `results[].success === false` 로 센다.
 * 회원 이름은 화면이 가진 목록에서 찾는다(서버 응답에는 이름이 없다).
 */
export const getBulkChangeCoachFailureSummary = (
  data: unknown,
  resolveMemberName?: (memberId: string) => string | undefined,
): { title: string; content: string } | null => {
  const result = getBulkChangeCoachResult(data, resolveMemberName);
  if (!result) return null;

  const lines = result.failed
    .slice(0, BULK_CHANGE_COACH_FAILURE_PREVIEW_LIMIT)
    .map((row) =>
      row.name === "회원 이름 미확인"
        ? `· ${row.reason}`
        : `· ${row.name}: ${row.reason}`,
    );
  const restCount = result.failureCount - lines.length;
  if (restCount > 0 && lines.length > 0) lines.push(`외 ${restCount}명`);

  return {
    title: `담당 강사 변경: ${result.totalCount}명 중 ${result.failureCount}명 실패`,
    content:
      lines.length > 0 ? lines.join("\n") : CHANGE_COACH_DEFAULT_ERROR_MESSAGE,
  };
};
